import React from 'react';
import {
  AdminLegendVariant,
  BookingState,
  ICalendarEventType,
  PendingCancellationTabTypes,
  PharmacyLegendVariant,
  RecurrenceActions,
  ServiceTypes,
} from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import {
  AdminNotesType,
  nameLabels,
  OtherScreens,
  ScreenTypes,
} from '../../helpers/Constants';
import ServiceWorkshiftDetails from '../SelfService/ServiceWorkshiftDetails';
import strings from '../../localization';
import Add from '../SelfService/Workshifts/AddWorkshift/Add';
import UpdateAvailability from '../Availability/UpdateAvailability';
import PharmacistProfile from '../SelfService/PharmacistProfile/PharmacistProfile';
import WorkshiftSummary from '../SelfService/WorkshiftSummary/WorkshiftSummary';
import BookingRequestsList from '../SelfService/BookingRequestsList/BookingRequestsList';
import ViewPharmacistRating from '../MyReplacements/PharmacistRating/ViewPharmacistRating/ViewPharmacistRating';
import UpdateWorkshift from '../SelfService/Workshifts/UpdateWorkshift';
import SubmitCounterOffer from '../SelfService/SubmitCounterOffer/SubmitCounterOffer';
import AvailableWorkshifts from '../Admin/AvailableWorkshifts';
import ViewDetails from '../Admin/ActiveUsers/ViewDetails';
import EditWorkshift from '../Admin/EditWorkshift';
import RequestList from '../Admin/RequestList';
import RequestedPharmacistList from '../Admin/RequestedPharmacistList';
import ViewRequestedDetails from '../Admin/RequestedPharmacistList/ViewRequestedDetails';
import ApproveBooking from '../Admin/ApproveBooking';
import MatchingAvailabilities from '../Admin/MatchingAvailabilities';
import BookingList from '../Admin/BookingList';
import ViewBookedDetails from '../Admin/BookingList/ViewBookedDetails';
import ViewAdminPharmacistRating from '../Admin/ViewAdminPharmacistRating';
import PharmacyViewProfile from '../Admin/Profile/Pharmacy/PharmacyViewProfile';
import PharmacistViewProfile from '../Admin/Profile/Pharmacist/PharmacistViewProfile/PharmacistViewProfile';
import ExpressBooking from '../Admin/ExpressBooking';
import PharmacyEditProfile from '../Admin/Profile/Pharmacy/PharmacyEditProfile/PharmacyEditProfile';
import PharmacistEditProfile from '../Admin/Profile/Pharmacist/PharmacistEditProfile/PharmacistEditProfile';
import AdminGeographicAvailability from '../Admin/Profile/AdminGeographicAvailability/AdminGeographicAvailability';
import PendingCancellationDetails from '../Admin/PendingCancellations/PendingCancellationDetails';
import { ICustomBack, IFromScreen } from '../../actions/drawerActions';
import PharmacistBookings from '../Admin/PharmacistBookings';
import PharmacyBookingDetails from '../Admin/PharmacyBookingDetails/PharmacyBookingDetails';
import ShowPharmacistAvailabilities from '../Admin/ShowPharmacistAvailabilities';
import MatchingWorkshifts from '../Admin/MatchingWorkshifts';
import AdminPharmacistHistory from '../Admin/PharmacistList/AdminPharmacistHistory';
import AdminReplacementNotes from '../Admin/PharmacistList/AdminReplacementNotes';
import ListOfPharmacies from '../Admin/PharmacistList/ListOfPharmacies';
import AdminCreateWorkshift from '../Admin/PharmacyList/AdminCreateWorkshift';
import ListOfPharmacists from '../Admin/PharmacyList/ListOfPharmacists';
import AdminPharmacyHistory from '../Admin/PharmacyList/ListOfPharmacists/AdminPharmacyHistory/AdminPharmacyHistory';
import AdminPharmacyNotes from '../Admin/PharmacyList/ListOfPharmacists/AdminPharmacyNotes/AdminPharmacyNotes';
import AdminCreateAvailability from '../Admin/PharmacistList/AdminCreateAvailability';
import AdminNotes from '../Admin/AdminNotes';
import AdminWatchlists from '../Admin/AdminWatchlists';
import BroadcastGroup from '../Admin/BroadcastList/BroadcastGroup';
import CreateVacation from '../common/CreateVacation';
import AvailabilityVacation from '../AvailabilityVacation';
import PharmacistReplacementProfile from '../PharmacistReplacementProfile';
import ViewUsersProfile from '../ViewUsersProfile';
import PharmagapTransactionDetails from '../Admin/PharmagapTransactions/PharmagapTransactionDetails';
import PendingRefundDetails from '../Admin/PendingRequestsList/PendingRefundDetails';
import WorkshiftList from '../WorkshiftList';
import WorkshiftSummaryList from '../SelfService/WorkshiftSummary/WorkshiftSummaryList';

const { self, full } = ServiceTypes;
const serviceType = ServiceHandler.getService();

export interface IScreenConfig {
  type?: ICalendarEventType;
  eventIds?: Array<string> | string;
  eventId?: string;
  date?: string;
  bookingState?: BookingState;
  workshiftId?: string;
  pharmacistId?: string;
  recType?: RecurrenceActions;
  recurrenceId?: string;
  title?: string;
  pharmacyId?: string;
  fromScreen?: IFromScreen;
  hasOwner?: boolean;
  back?: ICustomBack;
  extraParameters?: any;
}

export const screenConfigs = ({
  type = PharmacyLegendVariant.posted,
  eventIds = [],
  eventId = '',
  date = '',
  bookingState = BookingState.None,
  workshiftId = '',
  pharmacistId = '',
  recType = RecurrenceActions.occurence,
  recurrenceId = '',
  pharmacyId = '',
  fromScreen,
  hasOwner,
  back,
  extraParameters,
}: IScreenConfig) => {
  const {
    name,
    enableActions,
    notesType,
    enableDelete,
    isAutoSyncRecord,
    isAutoSyncIDMap,
    externalPharmacistId,
    longTermShiftContractId,
  } = extraParameters ?? {};

  const serviceBasedTitles = {
    [full]: {
      userBookingList: strings.pharmacistBookingList,
      userAvailabilities: strings.pharmacistAvailabilities,
      listOfUsers: strings.listOfPharmacists,
      userViewProfileTitle: strings.pharmacistProfileDetails,
      userDetails: strings.pharmacistDetails,
    },
    [self]: {
      userBookingList: strings.professionalBookingList,
      userAvailabilities: strings.professionalAvailabilities,
      listOfUsers: strings.listOfProfessionals,
      userViewProfileTitle: strings.professionalProfileDetails,
      userDetails: strings.professionalDetails,
    },
  };

  const {
    userAvailabilities,
    userBookingList,
    listOfUsers,
    userViewProfileTitle,
    userDetails,
  } = serviceBasedTitles[serviceType];

  return {
    [ScreenTypes.replacement]: [
      {
        title: strings.pharmacistProfileDetails,
        content: (
          <PharmacistReplacementProfile
            isAutoSyncRecord={isAutoSyncRecord}
            pharmacistId={pharmacistId}
          />
        ),
      },
    ],
    [ScreenTypes.workshiftList]: [
      {
        content: <WorkshiftList date={date} />,
      },
    ],
    [ScreenTypes.serviceWorkshiftDetails]: [
      {
        title: strings.workshiftDetails,
        content: (
          <ServiceWorkshiftDetails
            eventIds={eventIds as Array<string>}
            eventType={type}
            isAutoSyncIDMap={isAutoSyncIDMap}
          />
        ),
      },
      {
        title: strings.submitCounterOfferDef,
        content: (
          <SubmitCounterOffer
            eventIds={eventIds as Array<string>}
            id={eventId}
            type={type}
            isReports={extraParameters?.isReports}
          />
        ),
      },
    ],
    [ScreenTypes.addWorkshift]: [
      {
        title: strings.addWorkshifts,
        content: <Add date={date} />,
      },
    ],
    [ScreenTypes.addAvailability]: [
      {
        title: strings.addAvailabilityVacation,
        content: <AvailabilityVacation date={date} />,
      },
    ],
    [ScreenTypes.updateAvailability]: [
      {
        title: strings.updateAvailability,
        content: (
          <UpdateAvailability
            id={eventIds?.[0]}
            recType={recType}
            recurrenceId={recurrenceId}
          />
        ),
      },
    ],
    [ScreenTypes.workshiftSummary]: [
      {
        title: '',
        content: (
          <WorkshiftSummary
            bookingState={bookingState}
            eventIds={eventIds as Array<string>}
            type={type}
            preventInteraction={extraParameters?.preventInteraction}
          />
        ),
      },
      {
        title: '',
        content: (
          <BookingRequestsList
            date={date}
            eventIds={eventIds as Array<string>}
            type={type}
            eventId={eventId}
            preventInteraction={extraParameters?.preventInteraction}
            longTermShiftContractId={longTermShiftContractId}
          />
        ),
      },
      {
        title: userDetails,
        content: (
          <PharmacistProfile
            workshiftId={workshiftId}
            eventId={eventId}
            date={date}
            eventIds={eventIds as Array<string>}
            type={type}
            preventInteraction={extraParameters?.preventInteraction}
            longTermContractId={longTermShiftContractId}
            professionalId={pharmacistId}
          />
        ),
      },
      {
        title: strings.pharmacistRating,
        content: (
          <ViewPharmacistRating
            pharmacistId={pharmacistId}
            id={eventId}
            eventIds={eventIds as Array<string>}
            workshiftId={workshiftId}
            date={date}
            type={type}
            back={back}
            fromScreen={fromScreen}
          />
        ),
      },
      {
        title: '',
        content: (
          <WorkshiftSummaryList
            type={type as PharmacyLegendVariant}
            date={date}
            preventInteraction={extraParameters?.preventInteraction}
          />
        ),
      },
    ],
    [ScreenTypes.updateWorkshift]: [
      {
        title: '',
        content: (
          <WorkshiftSummary
            bookingState={bookingState}
            eventIds={eventIds as Array<string>}
            type={type}
          />
        ),
      },
      {
        title: strings.updateWorkshift,
        content: (
          <UpdateWorkshift
            eventIds={eventIds as Array<string>}
            recType={recType}
            recurrenceId={recurrenceId}
            type={type}
          />
        ),
      },
    ],
    [ScreenTypes.fullServiceUpdateWorkshift]: [
      {
        title: strings.updateWorkshift,
        content: (
          <UpdateWorkshift
            eventIds={eventIds as Array<string>}
            recType={recType}
            recurrenceId={recurrenceId}
            type={type}
          />
        ),
      },
    ],
    [ScreenTypes.availableWorkshift]: [
      {
        content: <AvailableWorkshifts date={date} viewOnly={false} />,
      },
      {
        title: strings.editWorkshift,
        content: (
          <EditWorkshift fromScreen={fromScreen} workshiftId={eventId} />
        ),
      },
      {
        title: strings.viewDetails,
        content: (
          <ViewDetails
            bookingIdList={eventIds as Array<string>}
            type={type as AdminLegendVariant}
            workshiftId={eventId}
            back={back}
            pharmacyId={pharmacyId}
            date={date}
            isAutoSyncRecord={isAutoSyncRecord}
          />
        ),
      },
      {
        title: strings.matchingAvailabilitiesOfWorkshift,
        content: (
          <MatchingAvailabilities
            workshiftId={eventId}
            type={type}
            fromScreen={fromScreen}
          />
        ),
      },
      {
        title: strings.expressBooking,
        content: (
          <ExpressBooking
            id={eventId}
            workshiftId={workshiftId}
            type={type as Exclude<AdminLegendVariant, AdminLegendVariant.total>}
            fromScreen={fromScreen}
          />
        ),
      },
    ],
    [ScreenTypes.requestedList]: [
      {
        content: <RequestList date={date} />,
      },
      {
        title: strings.back,
        content: (
          <RequestedPharmacistList
            workshiftId={eventId}
            isAutoSyncRecord={isAutoSyncRecord}
            bookingIdList={eventIds as Array<string>}
          />
        ),
      },
      {
        title: userDetails,
        content: (
          <ViewRequestedDetails
            workshiftId={workshiftId}
            bookingId={eventId}
            bookingIdList={eventIds as Array<string>}
            isAutoSyncRecord={isAutoSyncRecord}
            longTermShiftContractId={longTermShiftContractId}
            professionalId={pharmacistId}
            isExternalPharmacist={!!externalPharmacistId}
          />
        ),
      },
      {
        title: strings.approveBooking,
        content: (
          <ApproveBooking
            longTermShiftContractId={longTermShiftContractId}
            eventIds={eventIds as Array<string>}
            workshiftId={workshiftId}
            bookingId={eventId}
            fromScreen={fromScreen as OtherScreens}
            type={type}
            back={back}
            professionalId={pharmacistId}
            isAutoSyncRecord={isAutoSyncRecord}
          />
        ),
      },
    ],
    [ScreenTypes.bookingList]: [
      {
        content: <BookingList date={date} />,
      },
      {
        title: userDetails,
        content: (
          <ViewBookedDetails
            isAutoSyncRecord={isAutoSyncRecord}
            bookingIdList={eventIds as Array<string>}
            bookingId={eventId}
            isExternalPharmacist={!!externalPharmacistId}
          />
        ),
      },
      {
        title: strings.pharmacistAvgRating,
        content: (
          <ViewAdminPharmacistRating
            pharmacistId={pharmacistId}
            bookingId={eventId}
            eventIds={eventIds as Array<string>}
            workshiftId={workshiftId}
            type={type}
            back={back}
          />
        ),
      },
    ],
    [ScreenTypes.profile]: [
      {
        title: strings.pharmacyProfileDetails,
        content: (
          <PharmacyViewProfile
            back={back}
            hasOwner={hasOwner}
            fromScreen={fromScreen}
            pharmacyId={pharmacyId}
            enableActions={enableActions as boolean}
          />
        ),
      },
      {
        title: userViewProfileTitle,
        content: (
          <PharmacistViewProfile
            hasOwner={hasOwner}
            fromScreen={fromScreen}
            type={type}
            pharmacistId={pharmacistId}
            back={back}
            enableActions={enableActions as boolean}
            isExternal={!!externalPharmacistId}
          />
        ),
      },
      {
        title: strings.formatString(strings.editProfile, strings.pharmacy),
        content: (
          <PharmacyEditProfile
            fromScreen={fromScreen}
            hasOwner={hasOwner}
            back={back}
          />
        ),
      },
      {
        title: strings.formatString(
          strings.editProfile,
          nameLabels().roleLabel,
        ),
        content: (
          <PharmacistEditProfile
            fromScreen={fromScreen}
            type={type}
            back={back}
            pharmacistId={pharmacistId}
            hasOwner={hasOwner}
          />
        ),
      },
      {
        title: strings.pharmacistGeographicAvailability,
        content: (
          <AdminGeographicAvailability
            fromScreen={fromScreen}
            type={type}
            back={back}
            pharmacistId={pharmacistId}
            hasOwner={hasOwner}
          />
        ),
      },
    ],
    [ScreenTypes.pendingCancellation]: [
      {
        title: '',
        content: (
          <PendingCancellationDetails
            type={fromScreen as PendingCancellationTabTypes}
            cancellationRequestId={eventId}
          />
        ),
      },
    ],
    [ScreenTypes.pharmacistList]: [
      {
        title: strings.adminNotes,
        content: (
          <AdminNotes
            notesType={notesType as AdminNotesType}
            id={pharmacistId}
            back={back}
          />
        ),
      },
      {
        title: userBookingList,
        content: <PharmacistBookings pharmacistId={pharmacistId} />,
      },
      {
        title: strings.pharmacyBookingDetails,
        content: (
          <PharmacyBookingDetails
            pharmacistId={pharmacistId}
            bookingId={eventId}
            isAutoSyncRecord={isAutoSyncRecord}
          />
        ),
      },
      {
        title: userAvailabilities,
        content: <ShowPharmacistAvailabilities pharmacistId={pharmacistId} />,
      },
      {
        title: strings.matchingAvailabilitiesOfWorkshift,
        content: (
          <MatchingWorkshifts
            pId={pharmacistId}
            availabilityId={eventId}
            code={date}
          />
        ),
      },
      {
        title: strings.history,
        content: (
          <AdminPharmacistHistory
            pharmacistId={pharmacistId}
            isAutoSyncRecord={isAutoSyncRecord}
            pharmacyId={pharmacyId}
          />
        ),
      },
      {
        title: strings.notesForPharmacy,
        content: (
          <AdminReplacementNotes
            pharmacistId={pharmacistId}
            pharmacyId={pharmacyId}
            isAutoSyncRecord={isAutoSyncRecord}
          />
        ),
      },
      {
        title: strings.listOfPharmacies,
        content: <ListOfPharmacies pharmacistId={pharmacistId} />,
      },
      {
        title: strings.formatString(strings.createEvent, strings.availability),
        content: (
          <AdminCreateAvailability
            pharmacistId={pharmacistId}
            pharmacistName={name as string}
          />
        ),
      },
    ],
    [ScreenTypes.pharmacyList]: [
      {
        title: strings.createWorkshift,
        content: (
          <AdminCreateWorkshift
            back={back}
            pharmacyId={pharmacyId}
            name={name as string}
          />
        ),
      },
      {
        title: listOfUsers,
        content: <ListOfPharmacists pharmacyId={pharmacyId} />,
      },
      {
        title: strings.history,
        content: (
          <AdminPharmacyHistory
            pharmacistId={pharmacistId}
            pharmacyId={pharmacyId}
          />
        ),
      },
      {
        title: strings.notes,
        content: (
          <AdminPharmacyNotes
            pharmacistId={pharmacistId}
            pharmacyId={pharmacyId}
          />
        ),
      },
      {
        title: strings.adminNotes,
        content: (
          <AdminNotes
            notesType={notesType as AdminNotesType}
            id={pharmacyId}
            back={back}
          />
        ),
      },
    ],
    [ScreenTypes.adminWatchlists]: [
      {
        title: strings.watchlistedPharmacyWorkshifts,
        content: <AdminWatchlists pharmacistId={pharmacistId} />,
      },
    ],
    [ScreenTypes.broadcastList]: [
      {
        title: '',
        content: (
          <BroadcastGroup
            broadcastGroupId={eventId}
            enableDelete={enableDelete}
          />
        ),
      },
    ],
    [ScreenTypes.createVacation]: [
      {
        title: strings.createVacation,
        content: (
          <CreateVacation
            pharmacistId={pharmacistId}
            id={eventIds?.[0]}
            date={date}
            pharmacistName={name}
          />
        ),
      },
    ],
    [ScreenTypes.chat]: [
      {
        title: strings.profileDetails,
        content: <ViewUsersProfile personnelIds={eventIds as string[]} />,
      },
    ],
    [ScreenTypes.transactions]: [
      {
        title: '',
        content: <PharmagapTransactionDetails transactionId={eventId} />,
      },
    ],
    [ScreenTypes.pendingRefunds]: [
      {
        title: strings.pendingRefunds,
        content: <PendingRefundDetails refundRequestId={eventId} />,
      },
    ],
  };
};
